export const BRAND = {
  name: "LOGIBYTE TECHNOLOGIES",
  tagline: "Building Businesses. Creating Value.",
  email: "Operation@LogibyteTechnologies.com",
  logo: "/logo-128.webp",
  copyright: "© 2026 Logibyte Technologies — A Modern Business Enterprise. All Rights Reserved.",
};

// Central contact configuration — set `phone` once (e.g. "+91 98XXXXXX01")
// and the footer, contact page, tel: link, WhatsApp links and the floating
// WhatsApp button all activate automatically from this single value.
export const CONTACT = {
  email: "Operation@LogibyteTechnologies.com",
  phone: "+91 92899 72924",
  whatsappMessage:
    "Hello Logibyte Technologies, I would like to discuss a business / technology requirement.",
};

export const telHref = () =>
  CONTACT.phone ? `tel:${CONTACT.phone.replace(/[^\d+]/g, "")}` : null;

export const whatsappHref = () =>
  CONTACT.phone
    ? `https://wa.me/${CONTACT.phone.replace(/[^\d]/g, "")}?text=${encodeURIComponent(
        CONTACT.whatsappMessage
      )}`
    : null;

export const REGIONS = [
  "India",
  "United Arab Emirates",
  "United States",
  "United Kingdom",
  "Canada",
];

export const PHONE_CODES = [
  { value: "+91", label: "+91 (India)" },
  { value: "+971", label: "+971 (UAE)" },
  { value: "+1", label: "+1 (US / Canada)" },
  { value: "+44", label: "+44 (UK)" },
  { value: "+61", label: "+61 (Australia)" },
  { value: "+65", label: "+65 (Singapore)" },
  { value: "+49", label: "+49 (Germany)" },
  { value: "+", label: "Other" },
];

export { COUNTRIES } from "@/data/countries";

export const ENQUIRY_TYPES = [
  { value: "website", label: "Premium Business Website" },
  { value: "platform", label: "Custom Web Platform" },
  { value: "crm", label: "CRM & Workflow System" },
  { value: "automation", label: "Business Automation" },
  { value: "software", label: "Custom Business Software" },
  { value: "advisory", label: "Technology Advisory" },
  { value: "partnership", label: "Business Partnership" },
  { value: "corporate", label: "Corporate / General Enquiry" },
  { value: "other", label: "Other" },
];

export const BUDGET_OPTIONS = [
  "₹75,000 – ₹1,50,000",
  "₹1,50,000 – ₹3,00,000",
  "₹3,00,000 – ₹5,00,000",
  "₹5,00,000+",
  "Enterprise / Custom",
  "Not Decided Yet",
];

export const CONTACT_METHODS = ["Email", "Phone Call", "WhatsApp"];

export const SERVICES = [
  {
    slug: "business-websites",
    num: "01",
    label: "Digital Experiences",
    title: "Premium Business Websites",
    headline: "Premium digital experiences\nbuilt for serious businesses.",
    intro:
      "We design and develop premium, responsive business websites that combine strong brand presentation with clear customer journeys, enquiry infrastructure and a scalable technical foundation.",
    suitableFor: [
      "Corporate businesses",
      "Professional service companies",
      "Premium consumer brands",
      "Startups",
      "International businesses",
      "Growing enterprises",
    ],
    capabilities: [
      "Premium UI/UX",
      "Responsive development",
      "Corporate information architecture",
      "Contact and enquiry systems",
      "Lead forms",
      "Analytics integration",
      "Technical SEO foundation",
      "Performance optimization",
      "Security fundamentals",
      "CMS/content capability where required",
      "International-ready structure",
    ],
    pricingIndia: "₹75,000",
    pricingIntl: "US$1,500+",
    pricingNote:
      "Final pricing depends on project scope, page count, functionality, integrations and delivery requirements.",
    cta: "Discuss Your Website",
    faqs: [
      {
        q: "What does a premium business website cost?",
        a: "Engagements start from ₹75,000 in India and US$1,500 for international projects. Final pricing follows a requirement assessment based on scope, pages, functionality and integrations.",
      },
      {
        q: "How long does a website project take?",
        a: "Timelines depend on scope and content readiness. An estimated delivery schedule is provided with every proposal.",
      },
      {
        q: "Will the website work on mobile devices?",
        a: "Yes. Every website we deliver is designed responsively across desktop, tablet and mobile as a standard part of the engagement.",
      },
      {
        q: "Can you work with our existing brand and content?",
        a: "Yes. We can work with supplied brand assets and content, and can advise on structure and presentation where helpful.",
      },
      {
        q: "Do you provide maintenance after launch?",
        a: "Ongoing support plans are available separately, covering maintenance, updates and technical assistance.",
      },
      {
        q: "Do you work with international clients?",
        a: "Yes. We undertake suitable remote engagements for businesses outside India, with international indicative pricing shown in USD.",
      },
    ],
  },
  {
    slug: "custom-platforms",
    num: "02",
    label: "Custom Platforms",
    title: "Custom Web Platforms",
    headline: "Digital platforms built\naround your business model.",
    intro:
      "We develop custom web platforms for businesses that need more than a conventional website.",
    suitableFor: [
      "Businesses with unique customer journeys",
      "Service marketplaces",
      "Companies needing customer or vendor portals",
      "Operations-driven businesses",
    ],
    capabilities: [
      "Customer portals",
      "Service platforms",
      "Booking systems",
      "Marketplace workflows",
      "Vendor/provider portals",
      "Account dashboards",
      "Requirement submission systems",
      "Matching workflows",
      "Internal operational interfaces",
      "Custom web applications",
    ],
    pricingIndia: "₹1,50,000",
    pricingIntl: "US$3,500",
    pricingNote:
      "Final pricing follows requirement assessment and depends on workflows, integrations and platform complexity.",
    cta: "Discuss Your Platform",
    faqs: [
      {
        q: "When do I need a platform instead of a website?",
        a: "When your business requires logged-in experiences, structured workflows, portals or operational logic — not just information pages — a custom platform is usually the right foundation.",
      },
      {
        q: "Can the platform integrate with tools we already use?",
        a: "Where technically suitable, platforms can be designed around integrations with third-party systems and APIs.",
      },
      {
        q: "How are platform projects priced?",
        a: "Platform engagements start from ₹1,50,000 in India and US$3,500 internationally, with final pricing based on workflows, integrations and complexity.",
      },
      {
        q: "Can the platform grow with the business?",
        a: "Yes. Platforms are designed with a scalable technical foundation so capabilities can expand over time.",
      },
    ],
  },
  {
    slug: "crm-workflows",
    num: "03",
    label: "Business Systems",
    title: "CRM & Workflow Systems",
    headline: "Turn enquiries into\nstructured workflows.",
    intro:
      "We help businesses structure customer, sales and operational workflows through CRM implementation and custom workflow systems.",
    suitableFor: [
      "Businesses losing track of enquiries",
      "Sales teams managing leads manually",
      "Operations teams coordinating work over chat and email",
      "Growing service businesses",
    ],
    capabilities: [
      "Lead capture",
      "Lead distribution",
      "Sales pipelines",
      "Customer records",
      "Follow-up workflows",
      "Task allocation",
      "Team dashboards",
      "Status management",
      "Customer communication",
      "Operational coordination",
      "Reporting",
      "Role-based access",
      "Workflow automation",
    ],
    pricingIndia: "₹1,25,000",
    pricingIntl: "US$3,000",
    pricingNote:
      "Final pricing depends on workflow complexity, user roles, integrations and reporting requirements.",
    cta: "Build Your Workflow",
    faqs: [
      {
        q: "Can you work with our existing CRM?",
        a: "Where suitable, yes — we can design workflows, automation and integrations around existing CRM systems, or build a custom solution where the requirement justifies it.",
      },
      {
        q: "When should a business invest in a custom CRM?",
        a: "When off-the-shelf tools cannot reflect your actual sales and operational workflows, or when teams rely on spreadsheets and chat to manage customers.",
      },
      {
        q: "Can you migrate our existing customer data?",
        a: "Data migration can be included in scope where the existing data is accessible in a structured format.",
      },
      {
        q: "Do CRM engagements include automation?",
        a: "Yes. Follow-ups, notifications, task creation and status workflows can be automated as part of the system design.",
      },
      {
        q: "Is support available after the system goes live?",
        a: "Ongoing support plans are available for maintenance, improvements and operational assistance.",
      },
    ],
  },
  {
    slug: "automation",
    num: "04",
    label: "Automation",
    title: "Business Automation",
    headline: "Less repetitive work.\nBetter operating systems.",
    intro:
      "We design automation around repetitive business processes to improve speed, consistency and operational visibility.",
    suitableFor: [
      "Teams handling repetitive manual tasks",
      "Businesses with form-driven workflows",
      "Operations requiring timely notifications",
      "Companies connecting multiple tools",
    ],
    capabilities: [
      "Lead-routing automation",
      "Customer notifications",
      "Email workflows",
      "Internal task creation",
      "Follow-up automation",
      "Status notifications",
      "Form-to-CRM workflows",
      "Payment notifications",
      "API integrations",
      "Third-party application integrations",
      "Operational alerts",
    ],
    pricingIndia: "₹75,000",
    pricingIntl: "US$1,750",
    pricingNote:
      "Final pricing depends on the number of workflows, systems involved and integration complexity.",
    cta: "Explore Automation",
    faqs: [
      {
        q: "Which processes should we automate first?",
        a: "Typically the repetitive, high-volume steps: lead routing, follow-ups, notifications and form-to-system handoffs. We identify candidates during the assessment.",
      },
      {
        q: "Can automation connect our existing tools?",
        a: "Where APIs or integration options exist, yes. We review your current systems before proposing an approach.",
      },
      {
        q: "What does an automation engagement cost?",
        a: "Automation engagements start from ₹75,000 in India and US$1,750 internationally, based on workflow count and integration complexity.",
      },
      {
        q: "Will automation replace our team?",
        a: "No. Automation handles repetitive steps so your team can focus on judgement, customers and delivery.",
      },
    ],
  },
  {
    slug: "business-software",
    num: "05",
    label: "Custom Software",
    title: "Custom Business Software",
    headline: "Software designed around\nthe way your business works.",
    intro:
      "When off-the-shelf software cannot support a business's operating model, Logibyte can design and develop custom systems around specific workflows and requirements.",
    suitableFor: [
      "Businesses outgrowing generic software",
      "Operations with unique workflows",
      "Companies needing connected internal systems",
      "Service businesses with delivery complexity",
    ],
    capabilities: [
      "Internal operations software",
      "Customer management systems",
      "Staff/team systems",
      "Service-delivery platforms",
      "Administrative dashboards",
      "Reporting systems",
      "Customer portals",
      "Provider/vendor systems",
      "Business workflow applications",
    ],
    pricingIndia: "₹2,00,000",
    pricingIntl: "US$5,000",
    pricingNote:
      "Final pricing follows a detailed requirement assessment and solution design phase.",
    cta: "Discuss Your Requirement",
    faqs: [
      {
        q: "When does custom software make sense?",
        a: "When your operating model is constrained by generic tools and the workflow itself is a competitive advantage, a custom system is often the better long-term investment.",
      },
      {
        q: "How do custom software projects begin?",
        a: "With requirement mapping and solution design, so scope, architecture and commercials are clear before development begins.",
      },
      {
        q: "What does custom software cost?",
        a: "Custom software engagements start from ₹2,00,000 in India and US$5,000 internationally. Final pricing follows requirement assessment.",
      },
      {
        q: "Can the system evolve after launch?",
        a: "Yes. Ongoing support and development arrangements are available for continuous improvement.",
      },
    ],
  },
  {
    slug: "technology-advisory",
    num: "06",
    label: "Advisory",
    title: "Technology Advisory",
    headline: "Technology decisions\ngrounded in business reality.",
    intro:
      "We work with businesses to understand existing processes, identify technology gaps and develop practical roadmaps for digitisation, automation and operational improvement.",
    suitableFor: [
      "Businesses planning digitisation",
      "Leadership teams evaluating technology options",
      "Companies preparing for a platform or CRM project",
      "Founders seeking structured guidance",
    ],
    capabilities: [
      "Technology assessment",
      "Process mapping",
      "Digital transformation roadmap",
      "CRM planning",
      "Workflow architecture",
      "Automation opportunities",
      "Platform planning",
      "Operational technology strategy",
    ],
    pricingIndia: "₹25,000 per engagement",
    pricingIntl: "US$750 per engagement",
    pricingNote:
      "Advisory engagements are scoped individually based on depth and duration.",
    cta: "Book a Consultation",
    faqs: [
      {
        q: "What does an advisory engagement include?",
        a: "A structured review of your processes and systems, identification of gaps and opportunities, and a practical roadmap with clear recommendations.",
      },
      {
        q: "Is advisory required before a build project?",
        a: "Not always — but for complex requirements, an advisory phase reduces risk and clarifies scope before significant investment.",
      },
      {
        q: "What does advisory cost?",
        a: "Advisory engagements start from ₹25,000 in India and US$750 internationally, scoped by depth and duration.",
      },
    ],
  },
];

export const PRICING_TIERS = [
  {
    name: "Launch",
    price: "Starting ₹75,000",
    priceUsd: "Starting US$1,500+",
    intl: "International: US$1,500+",
    desc: "Designed for businesses requiring a premium digital presence or focused technology implementation.",
    scope: [
      "Premium business website",
      "Responsive experience",
      "Lead/enquiry infrastructure",
      "Analytics",
      "SEO foundation",
      "Core integrations",
    ],
    cta: "Start a Conversation",
  },
  {
    name: "Scale",
    price: "Starting ₹1,50,000",
    priceUsd: "Starting US$3,500+",
    intl: "International: US$3,500+",
    desc: "Designed for businesses requiring custom technology and connected workflows.",
    scope: [
      "Custom platforms",
      "CRM systems",
      "Customer portals",
      "Workflow automation",
      "Operational systems",
      "Third-party integrations",
    ],
    cta: "Discuss Your Project",
  },
  {
    name: "Enterprise",
    price: "Custom Proposal",
    priceUsd: "Custom Proposal",
    intl: "Scoped individually",
    desc: "For complex requirements involving:",
    scope: [
      "Custom software",
      "Multi-system integrations",
      "Complex operational workflows",
      "Large-scale platforms",
      "Long-term technology engagements",
    ],
    cta: "Request a Proposal",
  },
];

export const SUPPORT_PLANS = [
  {
    name: "Essential Care",
    price: "Starting ₹10,000/month",
    priceUsd: "Starting US$250+/month",
    intl: "International: US$250+/month",
    desc: "Suitable for basic ongoing maintenance and technical support.",
  },
  {
    name: "Business Care",
    price: "Starting ₹25,000/month",
    priceUsd: "Starting US$600+/month",
    intl: "International: US$600+/month",
    desc: "Suitable for businesses requiring priority support, maintenance and ongoing system improvements.",
  },
  {
    name: "Enterprise Support",
    price: "Custom Proposal",
    priceUsd: "Custom Proposal",
    intl: "International: Custom",
    desc: "For businesses requiring ongoing development, integrations, platform support or dedicated technology assistance.",
  },
];

export const HIW_STEPS = [
  { num: "01", label: "Discovery", copy: "We understand your business, current processes, customer journey, challenges and objectives." },
  { num: "02", label: "Requirement Mapping", copy: "We translate the requirement into clear functionality, workflows, integrations and project scope." },
  { num: "03", label: "Solution Design", copy: "We define the proposed user experience, system architecture and implementation approach." },
  { num: "04", label: "Build", copy: "The approved solution moves into design and development with structured progress reviews." },
  { num: "05", label: "Test & Launch", copy: "Core functionality, responsiveness and agreed workflows are reviewed before production release." },
  { num: "06", label: "Support & Evolve", copy: "Where ongoing support is included, we maintain, monitor and improve the solution as requirements evolve." },
];

export const START_STEPS = [
  "Submit your requirement.",
  "Our team reviews the requirement.",
  "We schedule a discovery discussion where appropriate.",
  "Scope and commercial proposal are prepared.",
  "Project begins after proposal acceptance and agreed commercial formalities.",
];

export const LOCATIONS = [
  {
    name: "India",
    headline: "Technology solutions for businesses across India.",
    copy: "We work with suitable businesses across India on digital platforms, business websites, CRM systems, automation and custom technology requirements.",
    note: "Including Delhi NCR, Mumbai, Bengaluru, Hyderabad, Pune, Chennai and Kolkata.",
  },
  {
    name: "UAE",
    headline: "Remote technology engagements for the UAE.",
    copy: "Premium technology solutions for businesses seeking remote digital development, platforms, workflow systems and automation support.",
  },
  {
    name: "United States",
    headline: "Remote engagements for US businesses.",
    copy: "Remote technology engagements for suitable businesses requiring premium web experiences, custom systems and operational technology.",
  },
  {
    name: "United Kingdom",
    headline: "Technology engagements for UK businesses.",
    copy: "Technology and digital-system engagements designed around individual business requirements.",
  },
  {
    name: "Canada",
    headline: "Remote technology services for Canada.",
    copy: "Remote technology and digital infrastructure services for suitable businesses.",
  },
  {
    name: "International",
    headline: "Working across borders.",
    copy: "Available for suitable remote technology engagements in other international markets. Indicative international pricing is shown in USD.",
  },
];

export const FAQ_CATEGORIES = [
  { key: "working", label: "Working with Logibyte" },
  { key: "services", label: "Services" },
  { key: "pricing", label: "Pricing" },
  { key: "process", label: "Project Process" },
  { key: "international", label: "International" },
  { key: "support", label: "Support" },
];

export const FAQS = [
  { c: "working", q: "What does Logibyte Technologies do?", a: "Logibyte Technologies builds and operates technology-enabled businesses and provides selected technology services to businesses requiring premium digital experiences, custom platforms, CRM systems, workflow automation and custom business software." },
  { c: "working", q: "What types of businesses do you work with?", a: "We consider requirements from startups, service businesses, growing companies and established organisations. Suitability depends on the requirement, scope, expected outcome and the type of technology engagement required." },
  { c: "working", q: "How do I start a project with Logibyte?", a: "Start by submitting your requirement through our Request Proposal form. Our team reviews the requirement and, where appropriate, arranges an initial discussion before scope and commercial terms are prepared." },
  { c: "services", q: "Do you develop premium business websites?", a: "Yes. We design and develop business websites where brand presentation, user experience, responsiveness, performance and conversion journeys are important to the project." },
  { c: "services", q: "Can Logibyte build a custom web platform?", a: "Yes. Suitable requirements can include customer portals, internal platforms, workflow applications, dashboards and other browser-based business systems." },
  { c: "services", q: "Can you build a custom CRM?", a: "Yes. CRM engagements can be designed around customer pipelines, lead management, follow-ups, team workflows, status tracking, reporting and other business-specific requirements." },
  { c: "services", q: "Can you automate existing business processes?", a: "Yes. We can evaluate repetitive or fragmented workflows and determine where technology, integrations or custom automation may improve the process." },
  { c: "services", q: "Do you develop custom business software?", a: "Yes. Where off-the-shelf software does not adequately fit a business requirement, we can evaluate a custom software approach." },
  { c: "services", q: "Do you provide technology advisory?", a: "Yes. Suitable advisory engagements can help businesses evaluate technology requirements, platform decisions, workflow design and implementation approaches." },
  { c: "services", q: "Do you provide digital marketing?", a: "Our primary focus is technology, digital systems and business software. Marketing-related requirements are considered only where they form part of a broader suitable engagement." },
  { c: "pricing", q: "How much does a Logibyte project cost?", a: "Project cost depends on scope, complexity, functionality, integrations, design requirements and expected delivery. Our Pricing page provides indicative starting ranges, while final commercial terms are prepared after understanding the requirement." },
  { c: "pricing", q: "Do you provide fixed pricing?", a: "Where a requirement can be clearly defined, a fixed project scope and commercial proposal may be possible. Requirements that evolve continuously may require a different engagement structure." },
  { c: "pricing", q: "Can international clients pay Logibyte?", a: "International commercial arrangements are discussed during the proposal process. Available payment methods and applicable terms depend on the engagement and invoicing requirements." },
  { c: "process", q: "How long does a project take?", a: "Timelines vary according to project scope and complexity. A focused business website may require significantly less time than a custom CRM, workflow platform or multi-module business system. Indicative timelines are discussed after requirement review." },
  { c: "process", q: "What happens after I submit my requirement?", a: "Our team reviews the requirement, determines whether it aligns with our capabilities and may arrange an initial discussion. Where the engagement is suitable, scope, deliverables and commercial terms are then prepared." },
  { c: "process", q: "Will I receive progress updates?", a: "Where applicable to the engagement, project execution includes structured communication and progress reviews so that agreed requirements can be reviewed during development." },
  { c: "process", q: "Do you test projects before launch?", a: "Yes. Applicable functionality, responsive behaviour, workflows and agreed requirements are reviewed before production release." },
  { c: "international", q: "Does Logibyte Technologies work with international businesses?", a: "Yes. Logibyte Technologies is India-based and can undertake suitable remote technology engagements for businesses in selected international markets." },
  { c: "international", q: "Which international markets can you work with?", a: "Suitable remote engagements may be considered from markets including the UAE, United States, United Kingdom, Canada, Australia and Singapore. Project acceptance depends on the requirement and engagement suitability." },
  { c: "international", q: "Do you have offices outside India?", a: "Logibyte Technologies is India-based. International engagements are currently handled remotely unless otherwise specifically agreed for a particular project." },
  { c: "international", q: "How do you manage projects across different time zones?", a: "Communication schedules, review points and project coordination can be agreed at the beginning of an international engagement based on the parties involved and the requirements of the project." },
  { c: "support", q: "Do you provide support after launch?", a: "Where post-launch support is included in the agreed engagement, we can maintain, monitor and improve the solution according to the applicable scope and commercial terms." },
  { c: "support", q: "Can an existing Logibyte solution be expanded later?", a: "Potentially, yes. Additional functionality can be evaluated as a separate requirement based on the existing architecture, technical feasibility and business objective." },
];

export const INSIGHT_CATEGORIES = [
  "Business Technology",
  "Digital Experiences",
  "CRM & Operations",
  "Automation",
  "Custom Software",
  "Customer Experience",
  "Business Building",
];

// All articles in this list are published and appear on /insights.
export const INSIGHTS = [
  {
    slug: "why-we-build-and-operate-our-own-service-businesses",
    category: "Business Systems",
    title: "Why We Build and Operate Our Own Service Businesses",
    intro:
      "Most technology companies build something, hand it over and leave. We chose the harder, slower, more honest job: staying.",
    date: "September 2026",
    readingTime: "6 min read",
    body: [
      {
        heading: "The problem was never demand",
        paragraphs: [
          "A few years ago, arranging a reliable home service in most Indian cities went something like this: three phone calls, two WhatsApp forwards, one recommendation from a neighbour, and a great deal of hoping. The demand was always there. What was missing was structure.",
          "We kept seeing the same pattern across categories — repairs, cleaning, staffing, local services of every kind. Customers were ready to pay. Providers were ready to work. What sat between them was disorder: no standard way to discover, compare, book, pay or be heard when something went wrong.",
        ],
      },
      {
        heading: "Ideas are cheap. Operations are the hard part.",
        paragraphs: [
          "Registering a domain takes ten minutes. Answering every customer properly for a year takes discipline. We did not want to be the company that designs a beautiful platform, hands over the keys and disappears before the first difficult week.",
          "So we made the decision that shapes everything at Logibyte: when we build a business, we stay and operate it. Customer acquisition, fulfilment, support, payments, the awkward Tuesday-morning problems — they are ours. That is where a service business is actually won or lost.",
        ],
      },
      {
        heading: "What operating really means",
        paragraphs: [
          "People hear 'technology company' and picture code. Our days look different. An enquiry comes in, someone qualifies it, the right professional is matched, the work is tracked, payment is collected, and the customer is asked how it went. Then we study what broke and fix the system, not the symptom.",
          "Technology does the coordination — the CRM, the workflows, the reminders nobody remembers to send. But the standard comes from caring about the boring details every single day.",
        ],
      },
      {
        heading: "It makes us better at client work, too",
        paragraphs: [
          "Because we run our own businesses, we feel the cost of a bad decision ourselves. A confusing form is not a design opinion here; it is lost revenue we can measure. A slow follow-up is not a process note; it is a customer who never came back.",
          "When we advise a client or build a platform for them, the advice comes from people who have sat on both sides of the table. We have never enjoyed being sold theory. We try not to sell it either.",
        ],
      },
      {
        heading: "The long view",
        paragraphs: [
          "None of this is fast. Building one service business properly means earning trust in a market that has been disappointed before. We would rather operate a few businesses well than launch ten and abandon them.",
          "That is the company we are building: one that finds a real customer problem, builds the structure around it, and stays long enough to be judged by the results. Everything else — the platforms, the automation, the design — exists in service of that.",
        ],
      },
    ],
  },

  {
    slug: "what-makes-a-business-website-feel-premium",
    featured: true,
    category: "Digital Experiences",
    title: "What Makes a Business Website Feel Premium?",
    intro:
      "Premium is not a colour palette or a font choice. It is the sum of dozens of deliberate decisions a visitor feels before they can name them.",
    date: "September 2026",
    readingTime: "4 min read",
    relatedService: "business-websites",
    body: [
      {
        heading: "Restraint communicates confidence",
        paragraphs: [
          "The most credible corporate websites say less. A clear headline, a short supporting statement and one obvious next step will outperform a page crowded with competing messages. When a company tries to say everything at once, visitors hear nothing.",
          "Restraint also applies to decoration. Every visual element should earn its place. If an animation, image or badge does not help a visitor understand the business or take the next step, it is noise.",
        ],
      },
      {
        heading: "Typography does the heavy lifting",
        paragraphs: [
          "Visitors rarely notice good typography consciously, but they immediately sense its absence. A considered pairing — an editorial serif for statements and a clean sans-serif for reading — creates hierarchy without decoration.",
          "Line length, spacing and size matter more than the typeface itself. Comfortable reading width and generous line height signal care.",
        ],
      },
      {
        heading: "Performance is part of design",
        paragraphs: [
          "A beautiful page that loads slowly does not feel premium. Fast rendering, smooth interaction and a layout that works equally well on a phone in Mumbai and a desktop in London are design features, not technical afterthoughts.",
        ],
      },
      {
        heading: "Clarity closes the loop",
        paragraphs: [
          "Within seconds, a visitor should understand what the company does, who it serves and how to start a conversation. If any of those three require guessing, the design has failed regardless of how it looks.",
        ],
      },
    ],
  },
  {
    slug: "when-does-a-growing-business-need-a-crm",
    category: "CRM & Operations",
    title: "When Does a Growing Business Need a CRM?",
    intro:
      "Most businesses do not decide to buy a CRM. They realise one day that customer information lives in too many places at once.",
    date: "September 2026",
    readingTime: "4 min read",
    relatedService: "crm-workflows",
    body: [
      {
        heading: "The warning signs",
        paragraphs: [
          "Enquiries arriving by email, phone and chat with no single record. Follow-ups dependent on individual memory. Sales status tracked in a spreadsheet only one person understands. These are not people problems — they are system problems.",
          "A useful test: if a team member is unavailable for a week, can the business still see exactly where every customer conversation stands? If not, the process depends on individuals rather than structure.",
        ],
      },
      {
        heading: "What a CRM actually changes",
        paragraphs: [
          "A well-designed CRM does three things. It captures every enquiry in one place. It makes the next step visible for each customer. And it creates a record that survives staff changes, busy periods and growth.",
          "The value compounds. Consistent follow-up improves conversion. Structured records improve handovers. Reporting reveals where enquiries actually come from and where they stall.",
        ],
      },
      {
        heading: "Off-the-shelf or custom",
        paragraphs: [
          "Standard CRM products suit many businesses. Custom systems become relevant when the operating model itself is distinctive — when lead routing, fulfilment or service workflows do not fit generic pipelines.",
          "The decision should follow the workflow, not the software demo. Map how enquiries really move through the business first, then choose the system that fits.",
        ],
      },
    ],
  },
  {
    slug: "understanding-the-cost-of-custom-web-development",
    category: "Technology Strategy",
    title: "Understanding the Cost of Custom Web Development",
    intro:
      "Why does one website cost a fraction of another that looks similar? The answer is almost never the design.",
    date: "September 2026",
    readingTime: "5 min read",
    relatedService: "custom-platforms",
    body: [
      {
        heading: "What actually drives cost",
        paragraphs: [
          "Four factors shape the price of a web project: the number of distinct page types, the functionality behind them, the systems they must connect to, and the quality bar for design and performance.",
          "A five-page company website and a five-page website with a customer portal, booking logic and CRM integration share almost nothing beneath the surface. Comparing them on page count alone leads to poor decisions.",
        ],
      },
      {
        heading: "Scope is a conversation, not a form",
        paragraphs: [
          "Reliable pricing requires understanding the requirement first. That is why serious technology providers give indicative starting points publicly and firm proposals only after a structured discussion.",
          "Be cautious of fixed quotes offered before anyone has asked about your workflows. A price agreed without understanding the work is usually revised later — or delivered against a much narrower interpretation than expected.",
        ],
      },
      {
        heading: "Cheapest is rarely cheapest",
        paragraphs: [
          "A low-cost build that cannot be extended, loads slowly or needs rebuilding within a year costs more than a considered build done once. The useful question is not \"what does it cost\" but \"what does it cost per year of useful service\".",
        ],
      },
      {
        heading: "How to prepare",
        paragraphs: [
          "Before requesting proposals, write down what the system must do, who will use it, and which existing tools it should connect with. Businesses that arrive with clarity receive sharper proposals and better outcomes.",
        ],
      },
    ],
  },
  {
    slug: "website-vs-custom-web-platform",
    category: "Technology Strategy",
    title: "How to Choose Between a Website and a Custom Web Platform",
    intro:
      "The wrong choice here is expensive in both directions: a platform when you needed a website wastes money, and a website when you needed a platform wastes the opportunity.",
    date: "September 2026",
    readingTime: "5 min read",
    relatedService: "custom-platforms",
    body: [
      {
        heading: "Start with what the visitor must do",
        paragraphs: [
          "A website informs. A platform does work. If visitors arrive, read, and get in touch, you need a website — a very good one, but a website. If they must log in, submit requirements, track progress, pay or manage something, you are describing a platform.",
          "Write down the five things a visitor should be able to do on your next build. If every answer is a form of 'read this' or 'contact us', stop there and invest in an excellent website. The moment 'log in' or 'track my request' appears on the list, the conversation has changed.",
        ],
      },
      {
        heading: "The honest difference in cost",
        paragraphs: [
          "A website is content with engineering around it. A platform is data, permissions, workflows and accounts — with content around it. Platforms carry user management, security and ongoing change, which is why they cost several times more and take longer.",
          "Be suspicious of anyone who quotes the same range for both. They are either underestimating the platform or overcharging for the website.",
        ],
      },
      {
        heading: "Grow from one into the other",
        paragraphs: [
          "The healthiest path we see: launch a strong website first, run the service manually behind it, and learn what customers actually ask for. When the manual process creaks — when your team spends its day copying details between chats and spreadsheets — you have earned the platform, and you know exactly what it must do.",
          "Building the platform before the process exists is how businesses end up with impressive software nobody uses.",
        ],
      },
    ],
  },
  {
    slug: "what-to-automate-first",
    category: "Automation",
    title: "Which Business Processes Should You Automate First?",
    intro:
      "The best first automation is never the most impressive one. It is the small, repetitive task your team quietly resents.",
    date: "September 2026",
    readingTime: "4 min read",
    relatedService: "automation",
    body: [
      {
        heading: "Automate the boring, not the broken",
        paragraphs: [
          "Automation amplifies whatever it touches. A clear, working process becomes faster and more consistent. A confused process becomes a faster, more consistent mess. Before automating anything, make sure a person can already do it well on purpose.",
        ],
      },
      {
        heading: "Three signs a task is ready",
        paragraphs: [
          "It happens often. It follows the same rules every time. And mistakes are easy to catch. Follow-up reminders, enquiry routing, status updates, invoice generation and report summaries usually pass all three tests on day one.",
          "Walk through your week and note every task where you copy information from one place to another. That list is your automation roadmap, roughly in order of value.",
        ],
      },
      {
        heading: "What to leave human",
        paragraphs: [
          "First conversations with customers. Complaints. Anything where judgment, tone or trust decides the outcome. Automation should protect your team's time for exactly these moments — not attempt to replace them.",
        ],
      },
      {
        heading: "Start smaller than you want to",
        paragraphs: [
          "Pick one workflow, automate it properly, and measure what changes. A first automation that visibly saves an hour a day builds the confidence — and the budget — for everything after it. Ten half-finished automations build only maintenance.",
        ],
      },
    ],
  },
  {
    slug: "website-vs-customer-portal",
    category: "Digital Experiences",
    title: "Website vs Customer Portal: Understanding the Difference",
    intro:
      "Your website speaks to strangers. A portal serves the people who already chose you. Confusing the two is how both end up mediocre.",
    date: "September 2026",
    readingTime: "4 min read",
    relatedService: "custom-platforms",
    body: [
      {
        heading: "Two different jobs",
        paragraphs: [
          "A website's job is persuasion and discovery: explain what you do, prove you are credible, and start a conversation. A portal's job is service: give existing customers their bookings, invoices, documents, requests and answers without making them call you.",
          "One is public and built for first impressions. The other sits behind a login and is built for the hundredth visit. They deserve different design energy.",
        ],
      },
      {
        heading: "When a portal pays for itself",
        paragraphs: [
          "Count how many customer conversations each week are really just status checks: 'where is my order', 'can you resend the invoice', 'what happens next'. Every one of those is a small tax on your team and a small wait for your customer.",
          "When that tax becomes noticeable, a portal stops being a luxury. Customers help themselves, support handles real problems, and the business looks organised without anyone staying late.",
        ],
      },
      {
        heading: "They work best together",
        paragraphs: [
          "The website wins the customer; the portal keeps them. Businesses that treat retention as a system — not a hope — usually find the portal quietly becomes their strongest reason for customers to stay.",
        ],
      },
    ],
  },
  {
    slug: "crm-workflow-automation-together",
    category: "CRM & Operations",
    title: "CRM, Workflow and Automation: How They Work Together",
    intro:
      "Three words that get sold separately but only work as one system. Understanding the difference saves you from buying the same capability twice.",
    date: "September 2026",
    readingTime: "5 min read",
    relatedService: "crm-workflows",
    body: [
      {
        heading: "Memory, path and hands",
        paragraphs: [
          "A CRM is memory: who the customer is, what they asked for, and where things stand. A workflow is the path: what should happen next, in what order, owned by whom. Automation is the hands: the routine steps the system performs without being asked.",
          "Most operational chaos comes from having one without the others. Memory without a path is a database nobody acts on. A path without automation depends on everyone remembering their step.",
        ],
      },
      {
        heading: "One enquiry, working properly",
        paragraphs: [
          "Watch a single enquiry move through a connected system. It arrives and the CRM records it — source, time, requirement. The workflow qualifies it and assigns an owner. Automation sends the customer an immediate acknowledgement and reminds the owner before the follow-up is late.",
          "Notice what the human actually did: the thinking and the talking. Everything around that conversation — recording, routing, reminding — happened because the system was designed to do it.",
        ],
      },
      {
        heading: "Design the flow before the tools",
        paragraphs: [
          "The failure pattern is buying three products and hoping they connect. The working pattern is mapping how an enquiry should travel through your business first, then choosing or building the smallest system that supports it.",
        ],
      },
    ],
  },
  {
    slug: "when-off-the-shelf-stops-working",
    category: "Custom Software",
    title: "When Off-the-Shelf Software Stops Working for Your Business",
    intro:
      "The clearest sign is not frustration with the software. It is the spreadsheet your team keeps on the side because the software does not quite fit.",
    date: "September 2026",
    readingTime: "5 min read",
    relatedService: "business-software",
    body: [
      {
        heading: "Workarounds are a signal",
        paragraphs: [
          "Every business runs two systems: the official one and the real one. When staff export data to fix it elsewhere, track statuses in personal notes, or maintain a parallel sheet 'just for our team', the tool is no longer supporting the process — the process is supporting the tool.",
        ],
      },
      {
        heading: "Paying to fight your own process",
        paragraphs: [
          "Generic software makes you run the business its way. That is fine while your process is ordinary. It becomes expensive when the way you work is part of why customers choose you — and you are paying licence fees for the privilege of bending that advantage out of shape.",
        ],
      },
      {
        heading: "Custom does not mean enormous",
        paragraphs: [
          "Custom software can be one small, sharp system that does exactly your workflow and nothing else. Start with the bottleneck — the handover, approval or report that eats the week — and build only that. A focused build costs less than a platform dream and proves its value in months, not years.",
        ],
      },
      {
        heading: "A fair test",
        paragraphs: [
          "Add up the hours your team loses each week to workarounds, double-entry and chasing information the system should already know. Multiply by fifty weeks. If that number stings, a conversation about custom software has already paid for itself.",
        ],
      },
    ],
  },
  {
    slug: "digital-systems-around-customer-journey",
    category: "CRM & Operations",
    title: "Building Digital Systems Around the Customer Journey",
    intro:
      "Customers experience your business as one continuous journey. Most systems are built as disconnected departments. The gap between those two is where customers are lost.",
    date: "September 2026",
    readingTime: "5 min read",
    relatedService: "crm-workflows",
    body: [
      {
        heading: "Map the journey before the system",
        paragraphs: [
          "Every service business has the same skeleton: discovery, enquiry, qualification, fulfilment, payment, support, feedback. Sketch yours on one page before choosing any tool. Systems should follow the journey the customer actually takes — not the org chart, and not the software demo.",
        ],
      },
      {
        heading: "Every handover is a leak",
        paragraphs: [
          "Each time responsibility passes — marketing to sales, sales to operations, operations to support — information quietly drops. The customer who explained their situation on the website repeats it on the phone, then again to the person who arrives to do the work.",
          "Good systems carry context forward automatically. The record built at the first enquiry should still be visible, enriched, at the final follow-up. Nothing important should live only in someone's memory or inbox.",
        ],
      },
      {
        heading: "The repetition test",
        paragraphs: [
          "Here is a simple standard: a customer should never have to explain their situation twice. If they do, some part of the journey is not connected, and that is the next thing to fix.",
        ],
      },
      {
        heading: "Improvement is built in",
        paragraphs: [
          "When the journey runs through connected systems, it produces something valuable by accident: honest data. Where enquiries come from, where they stall, which services generate complaints. The journey stops being a black box and becomes the thing you improve each month.",
        ],
      },
    ],
  },
  {
    slug: "before-developing-custom-software",
    category: "Custom Software",
    title: "What to Consider Before Developing Custom Business Software",
    intro:
      "Most custom software projects fail before development begins — in the weeks where the requirement was assumed instead of examined.",
    date: "September 2026",
    readingTime: "6 min read",
    relatedService: "business-software",
    body: [
      {
        heading: "Be honest about the problem",
        paragraphs: [
          "Not every operational pain is a software problem. If nobody owns the follow-up process, software will not create ownership — it will document its absence at considerable expense. Fix unclear responsibility first; then build tools for a process that actually exists.",
        ],
      },
      {
        heading: "Run it manually first",
        paragraphs: [
          "If a workflow cannot be operated with spreadsheets and phone calls, it is not understood well enough to automate. The manual version teaches you which steps matter, which exceptions appear weekly, and what the software must never get wrong. Skipping this lesson is the most expensive shortcut in custom development.",
        ],
      },
      {
        heading: "Budget for the second year",
        paragraphs: [
          "Launch day is the middle of the story, not the end. Software that is used attracts change requests, small improvements and the occasional repair. Plan and budget for ongoing care from the start — a system nobody maintains quietly becomes the next thing you need to replace.",
        ],
      },
      {
        heading: "Choose partners who ask awkward questions",
        paragraphs: [
          "A good development partner challenges your requirements, asks what happens when things go wrong, and occasionally tells you a feature is not worth building. One who agrees with everything is selling to you, not building for you. The awkward questions are the service.",
        ],
      },
    ],
  },
  {
    slug: "technology-for-service-business-operations",
    category: "Business Systems",
    title: "How Technology Can Improve Service-Business Operations",
    intro:
      "A service business sells a simple promise: someone capable will show up and do the work well. Technology's job is to make that promise keepable at scale.",
    date: "September 2026",
    readingTime: "5 min read",
    relatedService: "automation",
    body: [
      {
        heading: "Service businesses run on coordination",
        paragraphs: [
          "The product is a person, a time and a standard of work. Delivering that once is easy. Delivering it hundreds of times a month — across providers, locations and expectations — is a coordination problem, and coordination is exactly what well-built systems do.",
        ],
      },
      {
        heading: "Where technology earns its place",
        paragraphs: [
          "At the front, a website and structured enquiry capture mean no request goes unrecorded. In the middle, scheduling and matching put the right professional on the right job. Around the edges, automated communication keeps the customer informed, digital payment removes awkward collection, and feedback loops catch problems while they are still small.",
          "None of this replaces the human work. It removes the friction around the human work.",
        ],
      },
      {
        heading: "What changes for the customer",
        paragraphs: [
          "Faster answers, reliable arrival times, proof of what was done, easy payment and a channel that responds when something is wrong. Trust in a service business is built operationally — through kept promises — far more than through advertising.",
        ],
      },
      {
        heading: "What changes for the owner",
        paragraphs: [
          "Visibility. How many jobs ran today, which payments are pending, where complaints cluster, which providers customers ask for by name. Decisions that once ran on instinct start running on evidence — and the business becomes something you manage, rather than something that happens to you.",
        ],
      },
    ],
  },
  {
    slug: "prepare-before-technology-project",
    category: "Technology Strategy",
    title: "What Should a Business Prepare Before Starting a Technology Project?",
    intro:
      "The projects that go well are rarely the ones with the biggest budgets. They are the ones that arrived prepared.",
    date: "September 2026",
    readingTime: "4 min read",
    relatedService: "technology-advisory",
    body: [
      {
        heading: "Write the requirement in plain language",
        paragraphs: [
          "One honest page beats a forty-page specification written in jargon. Who will use this? What do they do today, step by step? What should happen instead? If you can answer those three questions clearly, any good technology partner can take it from there.",
        ],
      },
      {
        heading: "Gather the real materials",
        paragraphs: [
          "Existing logins, sample data, the reports you actually use, the spreadsheet that secretly runs the department. Reality teaches more than description. A developer who sees your real invoice format and your real customer list understands your business in an afternoon.",
        ],
      },
      {
        heading: "Nominate one decision-maker",
        paragraphs: [
          "Projects stall on committees. Feedback arrives in contradictory waves, nobody can say yes, and timelines quietly double. Choose one person with the authority to make calls and the availability to make them quickly — it is the single cheapest way to speed a project up.",
        ],
      },
      {
        heading: "Decide what success looks like",
        paragraphs: [
          "'Enquiries answered within ten minutes.' 'Five admin hours saved weekly.' 'Customers can see their own status.' A measurable target tells everyone when the work is done and whether it worked. Without one, a project is never finished — it just eventually stops.",
        ],
      },
    ],
  },
  {
    slug: "custom-software-vs-off-the-shelf",
    category: "Custom Software",
    title: "Custom Software vs Off-the-Shelf Software: How to Decide",
    intro:
      "The choice is rarely about technology. It is about how much of your business you are willing to bend around someone else's product.",
    date: "September 2026",
    readingTime: "6 min read",
    relatedService: "business-software",
    pull: "The question is not which option is cheaper this year. It is which one your business will still fit in three years.",
    body: [
      {
        heading: "Start with fit, not price",
        paragraphs: [
          "Off-the-shelf software wins on day one: it is available immediately, the upfront cost is predictable, and thousands of other businesses have already tested its edges. For standard requirements — accounting, email, simple task tracking — it is almost always the right answer.",
          "The comparison only becomes interesting when your process is part of your advantage. If the way you qualify, fulfil and support customers is meaningfully different from the average business, a tool built for the average business will ask you to compromise exactly where you are strongest.",
        ],
      },
      {
        heading: "Where off-the-shelf starts to hurt",
        paragraphs: [
          "The warning signs are operational before they are financial. Teams keep parallel spreadsheets because the tool cannot hold your data shape. Staff learn workarounds instead of workflows. Reports require exporting, cleaning and reassembling by hand.",
          "Each workaround feels small. Together they form a hidden tax — paid weekly, in hours, by the people you hired for better work.",
        ],
      },
      {
        heading: "What custom actually means",
        paragraphs: [
          "Custom software does not have to mean a platform with forty modules. Done well, it is a narrow, deliberate system that mirrors one workflow exactly: your pipeline stages, your approval logic, your terminology, your reports.",
          "The scope should be uncomfortable to describe in a generic product demo — that specificity is precisely the point.",
        ],
      },
      {
        heading: "Cost, speed and ownership",
        paragraphs: [
          "Be honest about the trade. Off-the-shelf is faster to start and cheaper in year one. Custom is slower to start, costs more upfront, and then stops charging you for seats, tiers and features you never use. You also own it: the roadmap answers to your business, not a vendor's pricing page.",
          "Whichever direction you choose, budget for change. Businesses evolve; the software around them must be able to follow.",
        ],
      },
      {
        heading: "A simple way to decide",
        paragraphs: [
          "Write down the three workflows that most affect your customers or your margins. If a standard product handles all three without workarounds, buy it and spend your energy elsewhere. If even one of them is being bent out of shape to fit a tool, that workflow is a candidate for something built around you.",
        ],
      },
    ],
  },
  {
    slug: "should-you-build-custom-web-platform",
    category: "Technology Strategy",
    title: "Should You Build a Custom Web Platform?",
    intro:
      "A website tells people what you do. A platform lets them do something with you. Knowing when to cross that line is a business decision, not a technical one.",
    date: "September 2026",
    readingTime: "5 min read",
    relatedService: "custom-platforms",
    pull: "Build the platform when the manual process behind the website starts to creak — not before.",
    body: [
      {
        heading: "When a website stops being enough",
        paragraphs: [
          "A website handles strangers gracefully: they arrive, understand you, and get in touch. Trouble begins after the enquiry — when your team retypes details into chats, tracks requests in personal inboxes, and customers call to ask what is happening.",
          "The moment that hidden manual work costs more than the website ever did, you are no longer looking at a website problem. You are looking at a platform requirement.",
        ],
      },
      {
        heading: "What a platform actually adds",
        paragraphs: [
          "Accounts, so customers and staff each see their own view. Workflows, so a request moves through defined stages instead of inboxes. Dashboards, so the state of the business is visible without asking anyone. Integrations, so payments, communication and reporting stop being separate chores.",
          "None of this is glamour. It is plumbing — and plumbing is what lets a service business scale without multiplying its coordination overhead.",
        ],
      },
      {
        heading: "The honest costs",
        paragraphs: [
          "A platform carries users, permissions and data, which means more design, more development, more testing and ongoing care after launch. Anyone quoting website prices for platform scope is guessing, and anyone proposing a platform for a requirement that a website solves is selling.",
          "The useful exercise is to map the journey: what should a customer be able to do, and what should your team never have to do by hand again?",
        ],
      },
      {
        heading: "A sensible path",
        paragraphs: [
          "Run the service manually behind a good website first. Learn what customers actually ask for and where your team's time goes. When the process is proven and the friction is measurable, build the platform around that evidence — it will be smaller, cheaper and far more likely to be used.",
        ],
      },
    ],
  },
  {
    slug: "signs-business-outgrown-spreadsheets",
    category: "Business Systems",
    title: "7 Signs Your Business Has Outgrown Spreadsheets",
    intro:
      "Spreadsheets are the best first system any business ever had. They are also quietly expensive once the business grows past them.",
    date: "September 2026",
    readingTime: "5 min read",
    relatedService: "business-software",
    pull: "Spreadsheets do not fail loudly. They fail quietly, one workaround at a time.",
    body: [
      {
        heading: "The information signs",
        paragraphs: [
          "One: the same customer, price or status exists in more than one sheet, and the copies disagree. Two: 'final_v3_FINAL.xlsx' — version confusion means nobody is certain which number is true.",
          "When your team spends time reconciling information instead of using it, the tool has become the task.",
        ],
      },
      {
        heading: "The process signs",
        paragraphs: [
          "Three: follow-ups depend on someone remembering to check a column. Four: producing a simple report — enquiries this month, jobs pending, revenue by service — takes an afternoon of manual assembly.",
          "Five: permissions. Either everyone can edit everything, or one person becomes the gatekeeper of every change. Neither is a system; both are risks.",
        ],
      },
      {
        heading: "The people signs",
        paragraphs: [
          "Six: teams work from different versions of reality — sales knows one status, operations another, the customer a third. Seven: administrative work grows faster than the business, and good people spend their days maintaining the sheet instead of serving customers.",
        ],
      },
      {
        heading: "What to do about it",
        paragraphs: [
          "You do not need to replace everything at once. Pick the workflow where the signs hurt most — usually lead tracking or job management — and give it a proper home: defined fields, clear stages, automatic reminders, one version of the truth.",
          "A small, well-designed system that the team actually uses beats a grand platform nobody opens. Start where the pain is, prove the value, then extend.",
        ],
      },
    ],
  },
  {
    slug: "business-website-redesign-plan",
    category: "Digital Experiences",
    title: "How to Plan a Business Website Redesign",
    intro:
      "Most redesigns fail in the planning, not the design. The order of decisions matters more than the quality of any single one.",
    date: "September 2026",
    readingTime: "6 min read",
    relatedService: "business-websites",
    pull: "A redesign that starts with colours ends with regret. Start with the audit.",
    body: [
      {
        heading: "Start with an honest audit",
        paragraphs: [
          "Before discussing how the new site should look, establish what the current one actually does. Which pages do visitors read? Where do enquiries come from? What do customers ask that the site fails to answer? The audit turns opinion into evidence.",
        ],
      },
      {
        heading: "Define the business objective",
        paragraphs: [
          "'Look more modern' is not an objective. 'Double qualified enquiries' or 'reduce pre-sales calls' are. A clear objective gives every later decision — structure, content, design, budget — something to answer to.",
          "Decide also who the site is really for. A page written for everyone persuades no one; know the visitor you most want to convert and let them shape the journey.",
        ],
      },
      {
        heading: "Structure before styling",
        paragraphs: [
          "Information architecture comes first: what pages exist, in what order, with what purpose. Then content — real headlines and real copy, not placeholders. Design that wraps around real content holds; design that precedes it tears.",
          "User experience and visual design follow structure, translating the journey into hierarchy, spacing, motion and restraint.",
        ],
      },
      {
        heading: "Protect what already works",
        paragraphs: [
          "If the current site has search visibility, a redesign can destroy it in a weekend. Map old URLs to new ones, preserve the content that earns traffic, carry over metadata, and test redirects before launch. SEO migration is a checklist, not a hope.",
        ],
      },
      {
        heading: "Test before you call it done",
        paragraphs: [
          "Review the site on the devices your customers actually use, at real connection speeds, with real content. Submit every form. Click every link. A redesign is finished when it works — not when it renders.",
        ],
      },
    ],
  },
  {
    slug: "remote-technology-partner",
    category: "Technology Strategy",
    title: "Working With a Remote Technology Partner: What Businesses Should Expect",
    intro:
      "Good remote collaboration is not cheaper communication. It is better-structured communication — designed, not improvised.",
    date: "September 2026",
    readingTime: "6 min read",
    relatedService: "technology-advisory",
    pull: "Distance does not create project risk. Ambiguity does.",
    body: [
      {
        heading: "Clarity is the job before the job",
        paragraphs: [
          "Remote work punishes vague requirements harder than in-person work, because there is no corridor conversation to rescue a misunderstanding. Expect a serious partner to ask uncomfortable questions early: who uses this, what happens today, what does success measure.",
          "That friction at the start is not slowness. It is the cheapest risk-reduction a project will ever buy.",
        ],
      },
      {
        heading: "Communication by design",
        paragraphs: [
          "Agree the rhythm before the work begins: written updates on a fixed schedule, review points at defined stages, one channel for decisions and one owner on each side. When communication has structure, time zones become a scheduling detail rather than a source of anxiety.",
          "Documentation matters more remotely, not less. Decisions, scope and changes should live in writing — shared, dated and findable — instead of in someone's memory of a call.",
        ],
      },
      {
        heading: "Reviews, not surprises",
        paragraphs: [
          "Expect to see the work as it forms: staged previews, progress reviews against the agreed scope, and structured testing before launch. A partner who shows you progress early is giving you the chance to correct cheaply. One who shows you only the finish line is asking you to pay for corrections at their most expensive.",
        ],
      },
      {
        heading: "After launch",
        paragraphs: [
          "Clarify support before you need it: what is covered, how issues are reported, and how improvements are evaluated. Software that is used will always attract change; the engagement should have a calm, agreed way of handling it.",
          "Judge a remote partner the same way you would judge a local one — by the clarity of their process and the quality of what they ship. Geography is the least interesting thing about either.",
        ],
      },
    ],
  },

];

export const PROPOSAL_SERVICES = [
  { value: "business-websites", label: "Premium Business Website" },
  { value: "custom-platforms", label: "Custom Web Platform" },
  { value: "crm-workflows", label: "CRM & Workflow System" },
  { value: "automation", label: "Business Automation" },
  { value: "business-software", label: "Custom Business Software" },
  { value: "technology-advisory", label: "Technology Advisory" },
  { value: "ongoing-support", label: "Ongoing Support" },
  { value: "not-sure", label: "Not Sure Yet" },
];

export const PROPOSAL_BUDGETS = [
  { value: "below-75k", label: "Below ₹75,000" },
  { value: "75k-150k", label: "₹75,000 – ₹1,50,000" },
  { value: "150k-300k", label: "₹1,50,000 – ₹3,00,000" },
  { value: "300k-500k", label: "₹3,00,000 – ₹5,00,000" },
  { value: "500k-plus", label: "₹5,00,000+" },
  { value: "international", label: "International / Discuss Requirement" },
];

export const PROPOSAL_CONTACT_METHODS = [
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "whatsapp", label: "WhatsApp" },
];

export const NAV_PRIMARY = [
  { label: "About", path: "/about" },
  {
    label: "Services",
    children: SERVICES.map((s) => ({
      label: s.title,
      path: `/services/${s.slug}`,
      num: s.num,
    })),
  },
  { label: "How It Works", path: "/how-it-works" },
  { label: "Pricing", path: "/pricing" },
  { label: "Businesses", path: "/businesses" },
  { label: "Locations", path: "/locations" },
  {
    label: "Resources",
    children: [
      { label: "FAQs", path: "/faq" },
      { label: "Insights", path: "/insights" },
    ],
  },
  { label: "Contact", path: "/contact" },
];
